import { useMemo } from 'react';
import {
  buildTableIds, categoryName, expenseCategoryName, findTable, itemName,
  selectIsOwner, sortExpenses, tableLabel, useAppStore,
} from '@restorant/core';
import type {
  AppSettings, Category, Expense, ExpenseCategory, MenuItem, RestaurantTable,
} from '@restorant/core';
import { useLang } from './i18n';

/**
 * The live menu, straight from the store. It starts as the bundled fallback and
 * is replaced by whatever the database holds on the first successful sync, so
 * screens must never import the old ITEMS/CATEGORIES constants.
 */
export function useCategories(includeInactive = false): Category[] {
  const categories = useAppStore((s) => s.categories);
  return useMemo(
    () => (includeInactive ? categories : categories.filter((c) => c.active)),
    [categories, includeInactive],
  );
}

export function useMenuItems(includeInactive = false): MenuItem[] {
  const items = useAppStore((s) => s.menuItems);
  return useMemo(
    () => (includeInactive ? items : items.filter((i) => i.active)),
    [items, includeInactive],
  );
}

export function useItemsInCategory(catId: string | null): MenuItem[] {
  const items = useMenuItems();
  return useMemo(() => items.filter((i) => i.cat === catId), [items, catId]);
}

/** Translated category name for the active language. */
export function useCategoryLabel() {
  const lang = useLang();
  const categories = useAppStore((s) => s.categories);
  return useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    return (catId: string) => categoryName(byId.get(catId), lang) || catId;
  }, [categories, lang]);
}

export function useCategoryIcon() {
  const categories = useAppStore((s) => s.categories);
  return useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    return (catId: string) => byId.get(catId)?.icon ?? 'grid';
  }, [categories]);
}

/* ---------------- floor plan ---------------- */

/**
 * The live floor plan, straight from the store. Like the menu it starts as the
 * bundled fallback and is replaced by the database on the first sync, so no
 * screen may import a hardcoded table list.
 */
export function useRestaurantTables(includeInactive = false): RestaurantTable[] {
  const tables = useAppStore((s) => s.restaurantTables);
  return useMemo(
    () => (includeInactive ? tables : tables.filter((r) => r.active)),
    [tables, includeInactive],
  );
}

/** Ids for the table picker, in floor order. */
export function useTableIds(): string[] {
  const tables = useAppStore((s) => s.restaurantTables);
  const legacy = useAppStore((s) => s.customTables);
  return useMemo(() => buildTableIds(tables, legacy), [tables, legacy]);
}

/** The record behind a table id, or undefined if it has since been removed. */
export function useFindTable() {
  const tables = useAppStore((s) => s.restaurantTables);
  return useMemo(() => (id: string) => findTable(tables, id), [tables]);
}

/**
 * What to call a table, in the active language. Resolves through the floor plan
 * so a renamed table reads correctly, and still names a deleted one on an old
 * receipt.
 */
export function useTableName() {
  const lang = useLang();
  const tables = useAppStore((s) => s.restaurantTables);
  return useMemo(() => {
    const byId = new Map(tables.map((r) => [r.id, r]));
    return (tableId: string) => tableLabel(lang, tableId, byId.get(tableId));
  }, [tables, lang]);
}

/* ---------------- settings ---------------- */

/** Brand, contact details and discount rules. Never hardcode these. */
export function useSettings(): AppSettings {
  return useAppStore((s) => s.settings);
}

/** The quick-discount buttons the owner configured. */
export function useDiscountPresets(): number[] {
  return useAppStore((s) => s.settings.discountPresets);
}

/* ---------------- dish names ---------------- */

/** Dish name in the active language, falling back to what the menu prints. */
export function useItemName() {
  const lang = useLang();
  return useMemo(() => (item: MenuItem | undefined) => itemName(item, lang), [lang]);
}

/* ---------------- money out (owner only) ---------------- */

/**
 * Whether this device is signed in as an owner of the restaurant it is working
 * in — which is what the Expenses tab is shown on.
 *
 * Worth being clear about what this is and is not. It decides what is drawn.
 * It does not decide what can be read: the expenses tables are owner-only in
 * RLS, so a worker who got past this would still be handed an empty result and
 * a 42501 on any write.
 */
export function useIsOwner(): boolean {
  return useAppStore(selectIsOwner);
}

/** The spending heads, live from the store. Bundle first, database wins. */
export function useExpenseCategories(includeInactive = false): ExpenseCategory[] {
  const heads = useAppStore((s) => s.expenseCategories);
  return useMemo(
    () => (includeInactive ? heads : heads.filter((c) => c.active)),
    [heads, includeInactive],
  );
}

/** Every recorded payment, newest first. */
export function useExpenses(): Expense[] {
  const expenses = useAppStore((s) => s.expenses);
  return useMemo(() => sortExpenses(expenses), [expenses]);
}

/**
 * What to call a spending head, in the active language. Resolves through the
 * live list, so a renamed head reads correctly everywhere at once.
 */
export function useExpenseHeadLabel() {
  const lang = useLang();
  const heads = useAppStore((s) => s.expenseCategories);
  return useMemo(() => {
    const byId = new Map(heads.map((c) => [c.id, c]));
    return (id: string | null) =>
      id ? expenseCategoryName(byId.get(id), lang) || id : '';
  }, [heads, lang]);
}

/** The icon for a head, falling back to the generic wallet. */
export function useExpenseHeadIcon() {
  const heads = useAppStore((s) => s.expenseCategories);
  return useMemo(() => {
    const byId = new Map(heads.map((c) => [c.id, c]));
    return (id: string | null) => (id ? (byId.get(id)?.icon ?? 'wallet') : 'wallet');
  }, [heads]);
}
